import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as profileService from '../services/profileService';
import * as instructorsService from '../services/instructorsService';
import { getPublicImageUrl } from '../utils/storage';

const {
  FiUser, FiMail, FiPhone, FiLock, FiBell, FiGlobe, FiCalendar, FiEdit2, FiTrash2, FiPlus,
  FiEye, FiEyeOff, FiLink, FiSettings, FiShield, FiBook
} = FiIcons;

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu', 'UTC',
  'Europe/London', 'Europe/Paris', 'Asia/Tokyo', 'Australia/Sydney'
];

const REMINDER_TIMING_OPTIONS = [
  { value: '24hr', label: '24 hours before' },
  { value: '1hr', label: '1 hour before' },
  { value: 'both', label: 'Both' },
];

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/50 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#00AAFF]/10 text-[#00AAFF]">
          <SafeIcon icon={Icon} className="w-5 h-5" />
        </div>
        <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function Profile() {
  const { user, profile, role, refreshProfile, resetPassword } = useAuth();
  const fileInputRef = useRef(null);
  const instructorImageInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile info
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');

  // Notifications
  const [eventReminders, setEventReminders] = useState(true);
  const [messageNotifications, setMessageNotifications] = useState(true);
  const [platformUpdates, setPlatformUpdates] = useState(true);
  const [reminderTiming, setReminderTiming] = useState('24hr');

  // Business profile (agent/partner)
  const [businessTitle, setBusinessTitle] = useState('');
  const [bio, setBio] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');

  // Student: registrations & agent
  const [registrations, setRegistrations] = useState([]);
  const [primaryAgent, setPrimaryAgent] = useState(null);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);

  // Instructors (agent/partner)
  const [instructorProfiles, setInstructorProfiles] = useState([]);
  const [instructorsLoading, setInstructorsLoading] = useState(false);
  const [instructorModal, setInstructorModal] = useState(null);
  const [instructorForm, setInstructorForm] = useState({ full_name: '', title: '', bio: '', visibility: 'private', profile_image_url: '', linkedin_url: '', website_url: '', credentials: '', specializations: [] });

  useEffect(() => {
    if (!profile) return;
    setFullName(profile.full_name ?? '');
    setPhone(profile.phone ?? '');
    setTimezone(profile.timezone ?? 'America/New_York');
    const prefs = profile.notification_preferences ?? {};
    if (typeof prefs === 'object') {
      setEventReminders(prefs.event_reminders !== false);
      setMessageNotifications(prefs.message_notifications !== false);
      setPlatformUpdates(prefs.platform_updates !== false);
      setReminderTiming(prefs.reminder_timing ?? '24hr');
    }
    const biz = profile.business_profile ?? {};
    if (typeof biz === 'object') {
      setBusinessTitle(biz.business_title ?? '');
      setBio(biz.bio ?? '');
      setWebsiteUrl(biz.website_url ?? '');
      setLinkedinUrl(biz.linkedin_url ?? '');
      setFacebookUrl(biz.facebook_url ?? '');
      setInstagramUrl(biz.instagram_url ?? '');
      setTwitterUrl(biz.twitter_url ?? '');
    }
  }, [profile]);

  useEffect(() => {
    if (role === 'student' && user?.id) {
      setRegistrationsLoading(true);
      profileService.getMyRegistrationsWithEvents().then(({ data, error }) => {
        setRegistrations(data ?? []);
        if (error) toast.error(error.message);
        setRegistrationsLoading(false);
      });
    }
  }, [role, user?.id]);

  useEffect(() => {
    if (role === 'student' && registrations.length > 0) {
      const agentId = registrations[0]?.primary_agent_id ?? null;
      if (agentId) {
        profileService.getAgentProfileById(agentId).then(({ data }) => setPrimaryAgent(data));
      }
    }
  }, [role, registrations]);

  useEffect(() => {
    if ((role === 'agent' || role === 'partner' || role === 'admin') && user?.id) {
      setInstructorsLoading(true);
      instructorsService.listMyInstructorProfiles().then(({ data, error }) => {
        setInstructorProfiles(data ?? []);
        if (error) toast.error(error?.message);
        setInstructorsLoading(false);
      });
    }
  }, [role, user?.id]);

  const profileCompletion = () => {
    let filled = 0;
    if (fullName?.trim()) filled++;
    if (profile?.email) filled++;
    if (phone?.trim()) filled++;
    if (profile?.profile_image_url) filled++;
    return Math.min(100, Math.round((filled / 4) * 100));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    const { success, error } = await profileService.updateMyProfile({
      full_name: fullName.trim() || null,
      phone: phone.trim() || null,
      timezone: timezone || null,
    });
    if (success) {
      await refreshProfile();
      toast.success('Profile updated');
    } else {
      toast.error(error?.message ?? 'Failed to update profile');
    }
    setSaving(false);
  };

  const handleProfileImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    const { data, error } = await profileService.uploadProfileImage(file, user.id);
    if (error) {
      toast.error(error.message ?? 'Upload failed');
      return;
    }
    const pathOnly = data.path || data.fullPath;
    const { success, error: updateErr } = await profileService.updateMyProfile({
      profile_image_url: pathOnly,
    });
    if (success) {
      await refreshProfile();
      toast.success('Photo updated');
    } else {
      toast.error(updateErr?.message ?? 'Failed to save photo');
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    const { success, error } = await profileService.updateNotificationPreferences({
      event_reminders: eventReminders,
      message_notifications: messageNotifications,
      platform_updates: platformUpdates,
      reminder_timing: reminderTiming,
    });
    if (success) {
      await refreshProfile();
      toast.success('Notification preferences saved');
    } else {
      toast.error(error?.message ?? 'Failed to save');
    }
    setSaving(false);
  };

  const handleSaveBusinessProfile = async () => {
    setSaving(true);
    const { success, error } = await profileService.updateMyProfile({
      business_profile: {
        business_title: businessTitle.trim() || undefined,
        bio: bio.trim() || undefined,
        website_url: websiteUrl.trim() || undefined,
        linkedin_url: linkedinUrl.trim() || undefined,
        facebook_url: facebookUrl.trim() || undefined,
        instagram_url: instagramUrl.trim() || undefined,
        twitter_url: twitterUrl.trim() || undefined,
      },
    });
    if (success) {
      await refreshProfile();
      toast.success('Business profile saved');
    } else {
      toast.error(error?.message ?? 'Failed to save');
    }
    setSaving(false);
  };

  const handleChangePassword = () => {
    const email = profile?.email ?? user?.email;
    if (!email) {
      toast.error('No email on file');
      return;
    }
    resetPassword(email).then(({ success, error }) => {
      if (success) toast.success('Check your email for the reset link');
      else toast.error(error ?? 'Failed to send reset email');
    });
  };

  const openInstructorModal = (inst = null) => {
    if (inst) {
      setInstructorForm({
        full_name: inst.full_name ?? '',
        title: inst.title ?? '',
        bio: inst.bio ?? '',
        visibility: inst.visibility ?? 'private',
        profile_image_url: inst.profile_image_url ?? '',
        linkedin_url: inst.linkedin_url ?? '',
        website_url: inst.website_url ?? '',
        credentials: inst.credentials ?? '',
        specializations: Array.isArray(inst.specializations) ? inst.specializations : [],
      });
      setInstructorModal(inst.id);
    } else {
      setInstructorForm({
        full_name: '', title: '', bio: '', visibility: 'private',
        profile_image_url: '', linkedin_url: '', website_url: '', credentials: '', specializations: [],
      });
      setInstructorModal('new');
    }
  };

  const closeInstructorModal = () => {
    setInstructorModal(null);
    instructorsService.listMyInstructorProfiles().then(({ data }) => setInstructorProfiles(data ?? []));
  };

  const saveInstructor = async () => {
    if (instructorModal === 'new') {
      const { data, error } = await instructorsService.createInstructorProfile({
        ...instructorForm,
        specializations: Array.isArray(instructorForm.specializations) ? instructorForm.specializations : (instructorForm.specializations ? String(instructorForm.specializations).split(',').map(s => s.trim()).filter(Boolean) : []),
      });
      if (error) {
        toast.error(error.message ?? 'Failed to create');
        return;
      }
      toast.success('Instructor profile created');
    } else {
      const { error } = await instructorsService.updateInstructorProfile(instructorModal, {
        ...instructorForm,
        specializations: Array.isArray(instructorForm.specializations) ? instructorForm.specializations : (instructorForm.specializations ? String(instructorForm.specializations).split(',').map(s => s.trim()).filter(Boolean) : []),
      });
      if (error) {
        toast.error(error.message ?? 'Failed to update');
        return;
      }
      toast.success('Instructor profile updated');
    }
    closeInstructorModal();
  };

  const deleteInstructor = async (id) => {
    if (!window.confirm('Delete this instructor profile? It will be removed from any events using it.')) return;
    const { error } = await instructorsService.deleteInstructorProfile(id);
    if (error) toast.error(error.message ?? 'Delete failed');
    else {
      setInstructorProfiles(prev => prev.filter(p => p.id !== id));
      toast.success('Deleted');
    }
  };

  const toggleVisibility = async (id, current) => {
    const next = current === 'public' ? 'private' : 'public';
    const { error } = await instructorsService.toggleInstructorVisibility(id, next);
    if (error) toast.error(error.message ?? 'Failed to update');
    else {
      setInstructorProfiles(prev => prev.map(p => p.id === id ? { ...p, visibility: next } : p));
      toast.success(`Visibility set to ${next}`);
    }
  };

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AAFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Account Profile</h1>
        <p className="text-slate-600 mt-1">Manage your profile, settings, and preferences.</p>
      </div>

      {/* Profile Information */}
      <SectionCard title="Profile Information" icon={FiUser}>
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="relative w-24 h-24 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-300 hover:border-[#00AAFF] transition-colors"
            >
              {getPublicImageUrl(profile?.profile_image_url, 'profile-images') ? (
                <img src={getPublicImageUrl(profile.profile_image_url, 'profile-images')} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <SafeIcon icon={FiUser} className="w-10 h-10 text-slate-400" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleProfileImageChange}
            />
            <span className="text-xs text-slate-500 mt-2">Click to upload</span>
          </div>
          <div className="flex-1 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Full name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#00AAFF] focus:border-[#00AAFF]"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={profile.email ?? ''}
                readOnly
                className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-600"
              />
              <p className="text-xs text-slate-500 mt-1">Email cannot be changed here.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#00AAFF] focus:border-[#00AAFF]"
                placeholder="+1 (555) 000-0000"
              />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-slate-700">Profile completion</span>
                <span className="text-slate-500">{profileCompletion()}%</span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#00AAFF] to-emerald-500 transition-all duration-300"
                  style={{ width: `${profileCompletion()}%` }}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={handleSaveProfile}
              disabled={saving}
              className="px-4 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0088dd] disabled:opacity-50 font-medium"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </SectionCard>

      {/* Account Settings */}
      <SectionCard title="Account Settings" icon={FiSettings}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <button
              type="button"
              onClick={handleChangePassword}
              className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-700"
            >
              <SafeIcon icon={FiLock} className="w-4 h-4" />
              Change password
            </button>
            <p className="text-xs text-slate-500 mt-1">We’ll send you an email to reset your password.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Timezone</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full max-w-xs px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#00AAFF]"
            >
              {TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={handleSaveProfile}
            disabled={saving}
            className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 font-medium"
          >
            {saving ? 'Saving…' : 'Save timezone'}
          </button>
        </div>
      </SectionCard>

      {/* Notification Preferences */}
      <SectionCard title="Notification Preferences" icon={FiBell}>
        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={eventReminders}
              onChange={(e) => setEventReminders(e.target.checked)}
              className="rounded border-slate-300 text-[#00AAFF] focus:ring-[#00AAFF]"
            />
            <span className="text-slate-700">Event reminders</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={messageNotifications}
              onChange={(e) => setMessageNotifications(e.target.checked)}
              className="rounded border-slate-300 text-[#00AAFF] focus:ring-[#00AAFF]"
            />
            <span className="text-slate-700">Message notifications</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={platformUpdates}
              onChange={(e) => setPlatformUpdates(e.target.checked)}
              className="rounded border-slate-300 text-[#00AAFF] focus:ring-[#00AAFF]"
            />
            <span className="text-slate-700">Platform updates</span>
          </label>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Reminder timing</label>
            <select
              value={reminderTiming}
              onChange={(e) => setReminderTiming(e.target.value)}
              className="w-full max-w-xs px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#00AAFF]"
            >
              {REMINDER_TIMING_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={handleSaveNotifications}
            disabled={saving}
            className="px-4 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0088dd] disabled:opacity-50 font-medium"
          >
            {saving ? 'Saving…' : 'Save preferences'}
          </button>
        </div>
      </SectionCard>

      {/* Student: Registration History */}
      {role === 'student' && (
        <SectionCard title="My Registration History" icon={FiCalendar}>
          {registrationsLoading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00AAFF]" /></div>
          ) : registrations.length === 0 ? (
            <p className="text-slate-500">You haven’t registered for any events yet.</p>
          ) : (
            <ul className="space-y-3">
              {registrations.map((reg) => {
                const ev = reg.event ?? {};
                const eventLink = ev.event_slug ? `/landing/${reg.event_id}` : `/dashboard/events/${reg.event_id}`;
                return (
                  <li key={reg.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <Link to={eventLink} className="font-medium text-[#00AAFF] hover:underline">
                        {ev.title ?? 'Event'}
                      </Link>
                      <p className="text-sm text-slate-500">
                        Registered {reg.created_at ? new Date(reg.created_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      reg.payment_status === 'completed' ? 'bg-green-100 text-green-800' :
                      reg.payment_status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {reg.payment_status ?? 'pending'}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>
      )}

      {/* Student: Agent/Partner Info */}
      {role === 'student' && (registrations.length > 0 || primaryAgent) && (
        <SectionCard title="My Agent / Partner" icon={FiUser}>
          {primaryAgent ? (
            <div className="space-y-1 text-slate-700">
              <p className="font-medium">{primaryAgent.full_name ?? 'Agent'}</p>
              {primaryAgent.email && <p className="text-sm">{primaryAgent.email}</p>}
              {primaryAgent.phone && <p className="text-sm">{primaryAgent.phone}</p>}
            </div>
          ) : (
            <p className="text-slate-500">No agent or partner assigned.</p>
          )}
        </SectionCard>
      )}

      {/* Agent/Partner: Business Profile */}
      {(role === 'agent' || role === 'partner' || role === 'admin') && (
        <SectionCard title="Business Profile" icon={FiLink}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Business title / name</label>
              <input
                type="text"
                value={businessTitle}
                onChange={(e) => setBusinessTitle(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#00AAFF]"
                placeholder="e.g. Senior Financial Advisor"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bio</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#00AAFF]"
                placeholder="Short bio for your profile"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Personal website</label>
              <input
                type="url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#00AAFF]"
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">LinkedIn</label>
                <input type="url" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg" placeholder="https://linkedin.com/..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Facebook</label>
                <input type="url" value={facebookUrl} onChange={(e) => setFacebookUrl(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg" placeholder="https://..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Instagram</label>
                <input type="url" value={instagramUrl} onChange={(e) => setInstagramUrl(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg" placeholder="https://..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Twitter</label>
                <input type="url" value={twitterUrl} onChange={(e) => setTwitterUrl(e.target.value)} className="w-full px-4 py-2 border border-slate-300 rounded-lg" placeholder="https://..." />
              </div>
            </div>
            <button
              type="button"
              onClick={handleSaveBusinessProfile}
              disabled={saving}
              className="px-4 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0088dd] disabled:opacity-50 font-medium"
            >
              {saving ? 'Saving…' : 'Save business profile'}
            </button>
          </div>
        </SectionCard>
      )}

      {/* Agent/Partner: Instructor Profiles */}
      {(role === 'agent' || role === 'partner' || role === 'admin') && (
        <SectionCard title="Instructor Profiles" icon={FiBook}>
          <p className="text-sm text-slate-600 mb-4">
            Create instructor profiles to attach to your events. Set visibility to <strong>Public</strong> to let other agents use your profile on their events.
          </p>
          <button
            type="button"
            onClick={() => openInstructorModal(null)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium mb-4"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            Create New Instructor Profile
          </button>
          {instructorsLoading ? (
            <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00AAFF]" /></div>
          ) : instructorProfiles.length === 0 ? (
            <p className="text-slate-500">No instructor profiles yet.</p>
          ) : (
            <div className="space-y-3">
              {instructorProfiles.map((inst) => (
                <div key={inst.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg bg-slate-50/50">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-200 flex-shrink-0 flex items-center justify-center">
                      {getPublicImageUrl(inst.profile_image_url, 'instructor-images') ? (
                        <img src={getPublicImageUrl(inst.profile_image_url, 'instructor-images')} alt={inst.full_name} className="w-full h-full object-cover" />
                      ) : (
                        <SafeIcon icon={FiUser} className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{inst.full_name}</p>
                      <p className="text-sm text-slate-600 line-clamp-1">{inst.bio || inst.title || '—'}</p>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${inst.visibility === 'public' ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-600'}`}>
                        {inst.visibility ?? 'private'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleVisibility(inst.id, inst.visibility)}
                      className="p-2 text-slate-500 hover:text-[#00AAFF]"
                      title={inst.visibility === 'public' ? 'Set private' : 'Set public'}
                    >
                      <SafeIcon icon={inst.visibility === 'public' ? FiEyeOff : FiEye} className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => openInstructorModal(inst)} className="p-2 text-slate-500 hover:text-[#00AAFF]" title="Edit">
                      <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => deleteInstructor(inst.id)} className="p-2 text-slate-500 hover:text-red-600" title="Delete">
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* Admin: Platform links */}
      {role === 'admin' && (
        <SectionCard title="Platform Management" icon={FiShield}>
          <div className="flex flex-wrap gap-3">
            <Link to="/dashboard/admin" className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 font-medium">
              User Management
            </Link>
            <Link to="/dashboard/admin" className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 font-medium text-slate-700">
              Platform Settings
            </Link>
            <Link to="/dashboard/admin" className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 font-medium text-slate-700">
              Audit Logs
            </Link>
          </div>
        </SectionCard>
      )}

      {/* Instructor create/edit modal */}
      {instructorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setInstructorModal(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              {instructorModal === 'new' ? 'New Instructor Profile' : 'Edit Instructor Profile'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Profile image</label>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => instructorImageInputRef.current?.click()}
                    className="w-20 h-20 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 hover:border-[#00AAFF] flex items-center justify-center overflow-hidden"
                  >
                    {getPublicImageUrl(instructorForm.profile_image_url, 'instructor-images') ? (
                      <img src={getPublicImageUrl(instructorForm.profile_image_url, 'instructor-images')} alt="Instructor" className="w-full h-full object-cover" />
                    ) : (
                      <SafeIcon icon={FiUser} className="w-8 h-8 text-slate-400" />
                    )}
                  </button>
                  <input
                    ref={instructorImageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !user?.id) return;
                      const { data, error } = await instructorsService.uploadInstructorImage(file, user.id);
                      if (error) {
                        toast.error(error.message ?? 'Upload failed');
                        return;
                      }
                      const pathOnly = data?.path || data?.fullPath;
                      if (pathOnly) setInstructorForm(f => ({ ...f, profile_image_url: pathOnly }));
                      toast.success('Image selected');
                      e.target.value = '';
                    }}
                  />
                  <span className="text-xs text-slate-500">Click to upload (instructor-images)</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  type="text"
                  value={instructorForm.full_name}
                  onChange={(e) => setInstructorForm(f => ({ ...f, full_name: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input
                  type="text"
                  value={instructorForm.title}
                  onChange={(e) => setInstructorForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                  placeholder="e.g. Lead Instructor"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Bio</label>
                <textarea
                  value={instructorForm.bio}
                  onChange={(e) => setInstructorForm(f => ({ ...f, bio: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                  placeholder="Short bio"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Expertise (comma-separated)</label>
                <input
                  type="text"
                  value={Array.isArray(instructorForm.specializations) ? instructorForm.specializations.join(', ') : instructorForm.specializations}
                  onChange={(e) => setInstructorForm(f => ({ ...f, specializations: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                  placeholder="e.g. Investing, Retirement"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">LinkedIn URL</label>
                <input type="url" value={instructorForm.linkedin_url} onChange={(e) => setInstructorForm(f => ({ ...f, linkedin_url: e.target.value }))} className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Website URL</label>
                <input type="url" value={instructorForm.website_url} onChange={(e) => setInstructorForm(f => ({ ...f, website_url: e.target.value }))} className="w-full px-4 py-2 border border-slate-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Visibility</label>
                <select
                  value={instructorForm.visibility}
                  onChange={(e) => setInstructorForm(f => ({ ...f, visibility: e.target.value }))}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                >
                  <option value="private">Private (only my events)</option>
                  <option value="public">Public (other agents can use)</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">Public profiles can be attached to events by other agents.</p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="button" onClick={saveInstructor} className="px-4 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0088dd] font-medium">
                {instructorModal === 'new' ? 'Create' : 'Save'}
              </button>
              <button type="button" onClick={closeInstructorModal} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 font-medium">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
